
import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useTheme } from '../../context/ThemeContext';
import { User as UserIcon, LogOut, ChevronDown, Sun, Moon, Menu, Settings } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface HeaderProps {
  onMenuClick?: () => void;
}

const Header: React.FC<HeaderProps> = ({ onMenuClick }) => {
  const { user, logout } = useAuth();
  const { isDarkMode, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [preferencesOpen, setPreferencesOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
        setPreferencesOpen(false);
      }
    };

    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setDropdownOpen(false);
        setPreferencesOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscapeKey);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, []);

  return (
    <header className="h-16 dark:bg-surface bg-light-surface border-b dark:border-border-color border-light-border-color flex items-center justify-between px-4 sm:px-6 flex-shrink-0">
      {/* Mobile menu button */}
      <button
        onClick={onMenuClick}
        className="lg:hidden p-2 rounded-lg dark:text-text-secondary text-light-text-secondary dark:hover:text-text-primary hover:text-light-text-primary dark:hover:bg-gray-700 hover:bg-gray-100 transition-colors"
        aria-label="Open menu"
      >
        <Menu className="w-6 h-6" />
      </button>
      
      {/* Spacer for desktop */}
      <div className="hidden lg:block" />
      
      {/* Right side controls */}
      <div className="flex items-center">
      <button
        onClick={toggleTheme}
        className="mr-4 p-2 rounded-lg dark:text-text-secondary text-light-text-secondary dark:hover:text-text-primary hover:text-light-text-primary dark:hover:bg-gray-700 hover:bg-gray-100 transition-colors relative overflow-hidden w-9 h-9"
        aria-label="Toggle theme"
      >
        <div className="relative w-full h-full">
          <Sun 
            className={`w-5 h-5 absolute top-1/2 -translate-y-1/2 transition-all duration-300 ${
              isDarkMode 
                ? 'left-1/2 -translate-x-1/2 opacity-100' 
                : 'left-full translate-x-0 opacity-0'
            }`}
          />
          <Moon 
            className={`w-5 h-5 absolute top-1/2 -translate-y-1/2 transition-all duration-300 ${
              !isDarkMode 
                ? 'left-1/2 -translate-x-1/2 opacity-100' 
                : '-left-full -translate-x-0 opacity-0'
            }`}
          />
        </div>
      </button>
      <div className="relative" ref={dropdownRef}>
        <button
          onClick={() => setDropdownOpen(!dropdownOpen)}
          className="flex items-center space-x-2 dark:text-text-primary text-light-text-primary dark:hover:text-white hover:text-gray-900 transition-colors"
        >
          <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center">
            <span className="font-semibold text-white">
              {user?.personalInfo.firstName?.[0]}
              {user?.personalInfo.lastName?.[0]}
            </span>
          </div>
          <span className="hidden md:inline text-sm">
            {user?.personalInfo.firstName} {user?.personalInfo.lastName}
          </span>
          <ChevronDown className={`w-4 h-4 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} />
        </button>
        {dropdownOpen && (
          <div className="absolute right-0 mt-2 w-48 dark:bg-surface bg-light-surface rounded-md shadow-lg py-1 border dark:border-border-color border-light-border-color z-50">
            <div>
              <button
                onClick={() => setPreferencesOpen(!preferencesOpen)}
                className="flex items-center justify-between w-full text-left px-4 py-2 text-sm dark:text-text-secondary text-light-text-secondary dark:hover:bg-gray-700 hover:bg-gray-100 dark:hover:text-white hover:text-gray-900"
              >
                <span className="flex items-center">
                  <Settings className="w-4 h-4 mr-2" />
                  Preferences
                </span>
                <ChevronDown className={`w-4 h-4 transition-transform ${preferencesOpen ? 'rotate-180' : ''}`} />
              </button>
              {preferencesOpen && (
                <div className="dark:bg-gray-800 bg-gray-50 py-1">
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      navigate('/preferences/dashboard');
                      setDropdownOpen(false);
                      setPreferencesOpen(false);
                    }}
                    className="flex items-center w-full text-left px-4 py-2 pl-10 text-sm dark:text-text-secondary text-light-text-secondary dark:hover:bg-gray-700 hover:bg-gray-100 dark:hover:text-white hover:text-gray-900"
                  >
                    Dashboard
                  </button>
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      navigate('/preferences/data-management');
                      setDropdownOpen(false);
                      setPreferencesOpen(false);
                    }}
                    className="flex items-center w-full text-left px-4 py-2 pl-10 text-sm dark:text-text-secondary text-light-text-secondary dark:hover:bg-gray-700 hover:bg-gray-100 dark:hover:text-white hover:text-gray-900"
                  >
                    Data Management
                  </button>
                </div>
              )}
            </div>
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                logout();
              }}
              className="flex items-center w-full text-left px-4 py-2 text-sm dark:text-text-secondary text-light-text-secondary dark:hover:bg-gray-700 hover:bg-gray-100 dark:hover:text-white hover:text-gray-900"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </a>
          </div>
        )}
      </div>
      </div>
    </header>
  );
};

export default Header;
